/**
 * How a roster cell is labelled and coloured.
 *
 * Extracted from ShiftRosterPage so the two things that must not drift are testable without
 * rendering a 31-column grid:
 *
 *  1. A leave day resolves to the *same* code and the *same* colour that the Leave module
 *     shows for that record. Both now read LeavePolicyService.getLeaveTypes() as the single
 *     source. The roster previously guessed the code from substrings of the type name and
 *     coloured it from a hardcoded table, so "Comp-Off" came out as "COMP" in amber here and
 *     "CO" in orange there — same record, two answers.
 *  2. A shift day is coloured from its configured ShiftMaster.color, never from a hardcoded
 *     class. OFF ("Weekly Off") is a real configured shift, and painting it from a CSS class
 *     meant editing its colour in Shift Configuration changed nothing in the grid.
 *
 * Shift and leave stay visually separable by *construction* rather than by hoping two
 * palettes don't collide: a shift fills the whole cell in a pale configured colour, a leave
 * is a saturated chip on a neutral cell. See leaveChipDisplay / shiftCellDisplay.
 */

import { ShiftMaster } from '../../types/shiftRoster';
import { LeavePolicyService } from '../../services/leavePolicyService';
// leaveTypeColor is not imported: leaveSeedColor below reads the configured list directly so it
// can fall back to this module's own palette for codes the master list omits. Both resolve to the
// same value for a configured type, which the tests assert.
import { leaveTypeName } from '../leave/pages/leaveShared';

/**
 * Seed colours for leave codes the configured master list does not define.
 *
 * DEFAULT_LEAVE_TYPES ships EL/CL/SL/ML/PL/CO only, yet real records carry "Unpaid Leave", and
 * Public Holiday surfaces here too. These are *seeds*, not final colours: everything shown in
 * the grid or the legend goes through toneFrom() below, so nothing is painted at full
 * saturation. Chosen in hue families well away from the shift palette — see the colour-distance
 * tests.
 */
const LEAVE_FALLBACK_PALETTE: Record<string, string> = {
  /**
   * Unpaid Leave (previously labelled LOP; see UNPAID_LEAVE_CODE).
   *
   * Bronze, not the crimson that reads as "unpaid" most obviously: a red-family seed painted a
   * chip 71 apart from Sick Leave's, and the requirement is that Unpaid Leave stay clearly
   * distinguishable from Sick and Earned Leave. Bronze is unclaimed by any configured leave type
   * and sits far from the amber Comp-Off uses.
   */
  UL: '#7c2d12',
  PH: '#a21caf',
};

/** Last resort for a leave code nobody has defined anywhere. */
const LEAVE_GENERIC_COLOR = '#6d28d9';

/**
 * Unpaid Leave's abbreviation.
 *
 * Renamed from LOP for display only, and safe to do so: nothing stores "LOP". Leave records
 * carry the *display name* in `type` ("Unpaid Leave", "Casual Leave"), the configured master
 * list (DEFAULT_LEAVE_TYPES / LeaveConfig) has never contained an LOP entry, and the old code
 * existed solely as this module's printed abbreviation. The alias table below still accepts
 * "LOP" as input so any historical record or hand-entered value keeps resolving.
 */
export const UNPAID_LEAVE_CODE = 'UL';

/** Public Holiday's seed, so holiday cells, the legend and any badge share one definition. */
export const PUBLIC_HOLIDAY_CODE = 'PH';

/**
 * Leave types withdrawn from the roster: Work From Home and On Duty.
 *
 * Both describe someone who is *working*, so the roster's job is to show the shift they are
 * working, not to replace it with a leave chip. Historical records are neither deleted nor
 * rewritten — isRosterLeaveType only stops them taking over the cell, and the cell's tooltip
 * still names them (see getCellState), so the data stays readable.
 */
const WITHDRAWN_LEAVE_CODES = new Set(['WFH', 'OD']);

/** Whether a leave record should own the roster cell, or defer to the employee's shift. */
export function isRosterLeaveType(type: string): boolean {
  return !WITHDRAWN_LEAVE_CODES.has(resolveLeaveCode(type));
}

/**
 * Type names the configured master list does not carry, mapped to their conventional code.
 *
 * Ordered and regex-based rather than substring `includes`, which is what mis-mapped
 * "Comp-Off": it contains neither "duty" nor "od" as a word, so it fell through to a
 * four-character truncation. Consulted only after the configured list, so renaming a leave
 * type in Leave Policies still wins over anything here.
 */
const LEAVE_NAME_ALIASES: Array<[RegExp, string]> = [
  // "LOP" is still accepted as *input* — the label changed, the recognised values did not.
  [/unpaid|loss\s*of\s*pay|\blop\b|\bul\b/i, UNPAID_LEAVE_CODE],
  [/work\s*from\s*home|\bwfh\b/i, 'WFH'],
  [/on\s*duty|\bod\b/i, 'OD'],
  [/public\s*holiday|\bholiday\b|\bph\b/i, PUBLIC_HOLIDAY_CODE],
  [/comp[\s-]*off|compensatory/i, 'CO'],
];

/** Display names for codes the configured master list does not carry. */
const LEAVE_FALLBACK_NAMES: Record<string, string> = {
  [UNPAID_LEAVE_CODE]: 'Unpaid Leave',
  [PUBLIC_HOLIDAY_CODE]: 'Public Holiday',
  WFH: 'Work From Home',
  OD: 'On Duty',
};

const norm = (v?: string): string => (v || '').trim().toLowerCase();

/**
 * The code to print for a leave record, resolved the same way the Leave module resolves it.
 *
 * Records store the *display name* ("Casual Leave", "Comp-Off"), not the code, so the
 * configured list is matched on name first and code second.
 */
export function resolveLeaveCode(type: string): string {
  const wanted = norm(type);
  if (!wanted) return 'LEAVE';

  const configured = LeavePolicyService.getLeaveTypes();
  const byName = configured.find(t => norm(t.name) === wanted);
  if (byName) return byName.code.toUpperCase();
  const byCode = configured.find(t => norm(t.code) === wanted);
  if (byCode) return byCode.code.toUpperCase();

  for (const [pattern, code] of LEAVE_NAME_ALIASES) {
    if (pattern.test(type)) return code;
  }

  // Nothing knows this type. Truncating is still better than printing a full name into a
  // 2ch cell, but it is a signal the type should be added to Leave Policies.
  return type.trim().slice(0, 4).toUpperCase();
}

/**
 * The single seed colour for a leave code — configured first, then the fallback palette.
 *
 * Exported so the legend and the cells provably read the same value rather than each holding
 * their own table, which is how Public Holiday came to be one colour in the legend and another
 * in the grid.
 */
export function leaveSeedColor(code: string): string {
  const configured = LeavePolicyService.getLeaveTypes()
    .find(t => t.code.toUpperCase() === code.toUpperCase());
  if (configured?.color) return configured.color;
  return LEAVE_FALLBACK_PALETTE[code.toUpperCase()] || LEAVE_GENERIC_COLOR;
}

/**
 * How strongly a seed is expressed.
 *
 * 'fill' covers a whole 42px cell, so it has to stay quiet enough that a month of them does not
 * overpower the grid — the complaint that prompted this. 'chip' is a small badge and can carry
 * more of the seed without shouting, which is also what keeps a leave chip from reading as a
 * shift fill.
 */
export type ToneStrength = 'fill' | 'chip';

export interface Tone {
  background: string;
  text: string;
  border: string;
}

/**
 * Surfaces the tints are mixed towards, per theme.
 *
 * These are the colours a roster cell *actually sits on*, and they must stay in step with
 * `.sr-cell` / `[data-theme="dark"] .sr-cell` in ShiftRosterPage.css. The dark value used to be
 * `#0b1220` while the cell was painted `#111827`, so every dark tone was tuned against a surface
 * two shades darker than the real one — the tints were measured to clear a background they never
 * actually sat on.
 */
const SURFACE_LIGHT = '#ffffff';
const SURFACE_DARK = '#111827';
const INK_LIGHT = '#0f172a';
const INK_DARK = '#f8fafc';

/**
 * Exported so a test can assert these still equal what `.sr-cell` is painted in the stylesheet.
 *
 * The whole defect this pairing addresses was a constant here drifting from the CSS, which no
 * amount of tuning the ratios could have revealed — the numbers looked right against the surface
 * the code believed in.
 */
export const CELL_SURFACE = { light: SURFACE_LIGHT, dark: SURFACE_DARK } as const;

/** The separation floor a painted fill is guaranteed to clear against its cell background. */
export const FILL_SEPARATION_FLOOR = 2.4;

/**
 * How far a fill must stand clear of the cell it sits on, as a WCAG contrast ratio.
 *
 * Not a text-legibility figure — it is the "this cell is coloured, that one is empty" signal, and
 * it is the thing that was missing. Measured on the reported palette, the old dark fills came out
 * at 1.69–2.15 against `#111827`, which on a 42px cell is indistinguishable from an unpainted one.
 * 2.4 is comfortably above that and still nowhere near a saturated block.
 */
const MIN_FILL_SEPARATION = FILL_SEPARATION_FLOOR;

/**
 * The luminance band a dark-mode fill is mapped into.
 *
 * A band, not a single target, and that distinction is load-bearing. Aiming every seed at one
 * luminance does fix the background-separation problem, but it throws away the seed's own
 * lightness — so two colours of the same hue differing only in lightness collapse onto each
 * other. Measured: Weekly Off `#e2e8f0` against slate `#64748b`, seeds 346 apart, painted to
 * within 4 of one another. That is the original defect wearing a different hat.
 *
 * Mapping the seed's lightness across a band preserves the *ordering* — a paler seed still paints
 * a paler fill — while lifting the whole range clear of the background.
 *
 * The bounds are the two hard constraints, not taste: the floor is the least luminance that
 * clears MIN_FILL_SEPARATION against `#111827` (2.45 at 0.095), and the ceiling stays under the
 * 0.25 "still reads as a dark tint rather than a glaring block" bound the tone tests assert.
 */
const DARK_FILL_LUMINANCE_MIN = 0.095;
const DARK_FILL_LUMINANCE_MAX = 0.235;

/**
 * Seed lightness range mapped across that band.
 *
 * Configured colours are picked to be legible as swatches, so they cluster in the upper half of
 * the lightness scale; mapping from 0 would waste most of the band on lightnesses nobody uses.
 */
const SEED_LIGHTNESS_MIN = 0.25;
const SEED_LIGHTNESS_MAX = 0.95;

/**
 * Keeps a washed-out seed from arriving as flat grey once its lightness is retargeted.
 *
 * Deliberately modest. A hard floor makes every pale slate arrive as a saturated blue, which both
 * misrepresents the configured colour and pushes distinct greys together — the floor should rescue
 * a colourless seed, not repaint a subtle one.
 */
const DARK_FILL_MIN_SATURATION = 0.25;


/*
 * How much of the seed survives into each tone. Tuned so a full-cell fill reads as a tint of
 * its type rather than as a block of colour, while the text keeps the seed's hue so the cell
 * still identifies itself at a glance.
 */
const MIX = {
  /*
   * fill is 0.30, not the 0.14 a "very subtle" reading would suggest.
   *
   * Measured: at 0.14 the six configured shift colours collapse into each other — the two
   * closest painted fills came out 8 apart, i.e. indistinguishable, because tinting scales the
   * *differences* between seeds down along with their intensity. 0.30 keeps the palest fill at
   * 0.889 luminance (still unmistakably a tint, nowhere near a saturated block) while leaving
   * distinct shifts visibly distinct. chip is pushed well clear of fill for the same reason a
   * leave chip must never read as a shift cell.
   */
  light: { fill: 0.30, chip: 0.62, border: 0.5, text: 0.72 },
  /*
   * Dark mode needs both a higher fill and a much stronger chip than light mode.
   *
   * Every tone is mixed towards the same surface, so a dark surface compresses distinct seeds into
   * a narrow band of near-black — the effect that made nineteen leave-chip/shift-fill pairs
   * confusable at fill 0.22 / chip 0.42, even though those palettes are 254 apart as seeds. Two
   * measured consequences drove the values below:
   *
   *  - fill 0.34, not 0.26: at 0.26 the six shift fills sat within luminance 0.024–0.069 and
   *    General against Night measured 81. At 0.34 that pair reaches 104.
   *  - chip 0.88: a chip must stay clearly stronger than any fill. Spreading the fills apart
   *    pushed them into the chips' space, dropping the worst leave pair to 86; letting a dark chip
   *    keep most of its seed restores it to 109. High chroma on a small badge against a dark
   *    background is not the "oversaturated cell" the requirement guards against.
   */
  dark: { fill: 0.34, chip: 0.88, border: 0.55, text: 0.8 },
} as const;

/**
 * The dark-mode whole-cell fill: the seed's hue and saturation, re-lit to a target luminance.
 *
 * Why this path does not use `mix` like every other tone. Mixing blends the seed towards the
 * surface, and on a *light* surface that works — white is far from every seed in luminance, so a
 * 30% blend still leaves the hues well separated from each other and from the background. On a
 * near-black surface it cannot work: every hue converges on the same near-black point, so the
 * blend scales the differences *between* seeds down along with their intensity. Measured on the
 * reported palette at the old 0.34 ratio: GEN, EMS and NS landed 1.69–2.15 contrast from the
 * `#111827` cell they sat on, and EMS against NS was 76 apart — mud, three ways.
 *
 * Keeping H and S and solving only for lightness fixes it by construction: the hue is the seed's
 * hue exactly, so a blue seed cannot come out as anything but blue, and the luminance target puts
 * every hue equally clear of the background. Saturation is floored so a pale seed still arrives
 * with visible colour rather than as grey.
 *
 * Bisection rather than a step loop because luminance is monotonic in lightness but strongly
 * non-linear across hues — a fixed step that suits blue overshoots badly on yellow.
 */
function darkToneFromSeed(
  seed: string,
  luminanceMin: number,
  luminanceMax: number,
  saturationFloor: number
): string {
  const hsl = hexToHsl(seed);
  if (!hsl) return SURFACE_DARK;

  const h = hsl.h;
  const s = Math.max(hsl.s, saturationFloor);

  // Where this seed sits in the configured lightness range, 0 (darkest) to 1 (palest).
  const position = Math.max(0, Math.min(1,
    (hsl.l - SEED_LIGHTNESS_MIN) / (SEED_LIGHTNESS_MAX - SEED_LIGHTNESS_MIN)
  ));
  const targetLuminance = luminanceMin + (luminanceMax - luminanceMin) * position;

  const at = (l: number) => hslToHex({ h, s, l });

  /*
   * Solve lightness for the target luminance by bisection. Luminance is monotonic in lightness
   * but strongly non-linear across hues — yellow reaches a given luminance at a far lower
   * lightness than blue does — so a fixed step that suits one hue overshoots badly on another.
   */
  let lo = 0.02;
  let hi = 0.98;
  let best = at(0.34);
  for (let step = 0; step < 24; step++) {
    const mid = (lo + hi) / 2;
    best = at(mid);
    if (relativeLuminance(best) < targetLuminance) lo = mid;
    else hi = mid;
  }

  let lightness = (lo + hi) / 2;

  /*
   * Escape the mid-luminance dead band.
   *
   * Around luminance 0.20 *neither* near-white nor near-black text reaches 4.5:1 — measured 4.24
   * on one fill. The original code answered this by stepping the background towards the surface,
   * which is the very move that made fills invisible. Moving *along* the band instead fixes the
   * text without giving up the separation: a fill steps darker (white text improves and it stays
   * above the separation floor, which is itself comfortably outside the dead band), a chip steps
   * brighter. Hue is untouched either way.
   */
  // Only fills reach this function, so escaping the dead band always means going darker.
  const direction = -1;
  for (let step = 0; step < 40; step++) {
    const candidate = readableTextOn(best);
    if (contrastRatio(best, candidate) >= AA_CONTRAST) break;
    lightness = Math.max(0.02, Math.min(0.98, lightness + direction * 0.015));
    best = at(lightness);
  }

  /*
   * A seed can be so dark that even its band target leaves it short of the separation floor.
   * Lifting lightness further is safe — it cannot change the hue — so the guarantee holds for any
   * configured colour, not only the ones that happen to be bright.
   */
  for (let step = 0; step < 30 && contrastRatio(best, SURFACE_DARK) < MIN_FILL_SEPARATION; step++) {
    lightness = Math.min(0.98, lightness + 0.02);
    best = at(lightness);
  }

  return best;
}

/**
 * Turn a configured seed colour into the calm, theme-aware tone actually painted.
 *
 * This is the one place a displayed colour is decided. Configuration supplies a seed; the
 * legend and the roster cell both call this with the same seed and strength, so they cannot
 * disagree — and toning down the palette is a change to these ratios rather than a hunt through
 * hardcoded tables.
 *
 * Three of the four theme/strength combinations blend the seed towards the surface, which is the
 * right model on a light background and for the small strongly-coloured badges. The fourth — a
 * whole-cell fill in dark mode — is re-lit instead; see darkFillFromSeed for why blending cannot
 * work against a near-black surface.
 */
export function toneFrom(seed: string, isDark = false, strength: ToneStrength = 'fill'): Tone {
  const surface = isDark ? SURFACE_DARK : SURFACE_LIGHT;
  const ink = isDark ? INK_DARK : INK_LIGHT;
  const ratios = isDark ? MIX.dark : MIX.light;

  const border = mix(seed, surface, ratios.border);
  // Text is the seed pulled hard towards the theme's ink: dark enough to read on a pale tint
  // (or light enough on a dark one) while still carrying the type's hue.
  const seedText = mix(seed, ink, ratios.text);

  /*
   * Only the dark *fill* is re-lit. Chips keep the original blend in both themes, deliberately.
   *
   * Re-lighting chips as well was tried and reverted: the leave palette's relationships are tuned
   * against each other (Unpaid Leave was moved to bronze specifically to clear Sick Leave), and
   * mapping every chip into a luminance band compressed the pairs that share a hue — Comp-Off
   * against Unpaid Leave, both around hue 21, fell to 77 where the palette guarantees 90. Chips
   * were never the reported problem: at 88% of the seed they already carry their colour plainly on
   * a dark cell, which is why the same seed looked right as a chip and wrong as a fill.
   */
  const isDarkFill = isDark && strength === 'fill';
  let background = isDarkFill
    ? darkToneFromSeed(seed, DARK_FILL_LUMINANCE_MIN, DARK_FILL_LUMINANCE_MAX, DARK_FILL_MIN_SATURATION)
    : mix(seed, surface, strength === 'fill' ? ratios.fill : ratios.chip);

  let text = contrastRatio(background, seedText) >= AA_CONTRAST
    ? seedText
    : readableTextOn(background);

  /*
   * Nudge the background towards the surface until the text clears AA.
   *
   * A guarantee rather than a tuned constant. Both palettes are HR-editable, and a seed whose
   * tone lands mid-luminance leaves *neither* near-black nor near-white text at 4.5:1 — one
   * dark-mode chip measured 4.34. Stepping towards the surface moves the tone out of that dead
   * band while keeping the seed's hue, so no colour has to be hand-corrected. Bounded, and the
   * loop only ever runs for the rare mid-tone.
   *
   * Skipped for the dark fill only, and that exclusion is the point of this whole change: stepping
   * a dark fill towards the surface is precisely how it ends up indistinguishable from the empty
   * cell beside it. That path escapes the same dead band by moving *along* its luminance band
   * instead (see darkToneFromSeed), so it arrives here already clear of it — while every
   * mix-derived tone, dark chips included, still needs this repair.
   */
  if (!isDarkFill) {
    for (let step = 0; step < 12 && contrastRatio(background, text) < AA_CONTRAST; step++) {
      background = mix(background, surface, 0.88);
      text = contrastRatio(background, seedText) >= AA_CONTRAST
        ? seedText
        : readableTextOn(background);
    }
  }

  return { background, border, text };
}

export interface LeaveChipDisplay {
  code: string;
  /** Full name for tooltips/legend — the configured one when there is one. */
  label: string;
  background: string;
  text: string;
  border: string;
}

/**
 * Chip styling for a leave day, from the configured leave type via the shared tone pipeline.
 */
export function leaveChipDisplay(type: string, isDark = false): LeaveChipDisplay {
  const code = resolveLeaveCode(type);
  const configured = LeavePolicyService.getLeaveTypes()
    .find(t => t.code.toUpperCase() === code);
  const tone = toneFrom(leaveSeedColor(code), isDark, 'chip');

  return {
    code,
    label: configured
      ? leaveTypeName(code)
      : (LEAVE_FALLBACK_NAMES[code] || type || code),
    background: tone.background,
    text: tone.text,
    border: tone.border,
  };
}

/** Public Holiday's tone, drawn from the same seed the legend uses. */
export function holidayTone(isDark = false, strength: ToneStrength = 'fill'): Tone {
  return toneFrom(leaveSeedColor(PUBLIC_HOLIDAY_CODE), isDark, strength);
}

export interface ShiftCellDisplay {
  /** Toned background derived from the configured colour; undefined when stale. */
  background?: string;
  /** Contrast-safe text colour for that background. */
  text: string;
  /** Border tone, for the legend swatch. */
  border?: string;
  /** The configured shift name, for tooltips and the legend. */
  label?: string;
  /** The raw configured colour, before toning — for diagnostics and tests. */
  seed?: string;
  /** True when no active ShiftMaster carries this code. */
  isStale: boolean;
  /**
   * True when the shift exists but carries no usable colour, so `background` is a derived
   * stand-in rather than anything HR chose.
   *
   * Distinct from `isStale`, and the distinction matters: a stale code is a data error the roster
   * must not paint over, while a shift with a blank `color` is a real shift that simply has nothing
   * to paint with. That second case used to return no background at all — identical on screen to an
   * unpainted cell, and *not* reported by the roster's staleness check, so it produced a cell with
   * no colour and no warning anywhere. The one failure mode with no signal at all.
   */
  usingFallbackColor?: boolean;
}

/**
 * A stand-in colour for a shift whose `color` is blank, derived from its own code.
 *
 * Deterministic and hue-spread: the code's characters are hashed to a hue, so two shifts missing a
 * colour still get different stand-ins, and the same shift always gets the same one. Saturation and
 * lightness are fixed mid-range and then run through the normal tone pipeline, so a stand-in obeys
 * every rule a configured colour does — separation from the cell background, AA text contrast.
 *
 * A stand-in, not a fix: `usingFallbackColor` is set so the UI can tell HR to set a real colour.
 * The alternative — leaving the cell unpainted — is what made this invisible in the first place.
 */
export function fallbackSeedForCode(code: string): string {
  let hash = 0;
  const key = norm(code) || 'shift';
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) % 360;
  return hslToHex({ h: hash, s: 0.62, l: 0.55 });
}

/**
 * The active ShiftMaster a stored roster value refers to — by code first, then by name.
 *
 * One resolver, used by both the cell's colour and the roster's staleness check, because those
 * two answering differently is what the AFS report turned out to be: the legend iterates the
 * configured shifts and always holds an exact `shiftCode`, while a cell carries whatever string
 * was *stored* against the employee or the assignment. When those disagree the cell gets no fill
 * at all and falls back to the plain themed background — configured colour on the badge, bare dark
 * cell in the grid, which is exactly what was reported.
 *
 * Matching the name as well as the code is a data-model fact, not a special case for one shift.
 * `resolveShiftCode` in userLoader falls back to the legacy `custom:shift` / `custom:primaryShift`
 * Cognito attributes, which that module documents as permanent and as holding values from before
 * the move to UserProfiles — free text that can carry a shift's *name* ("Afternoon Shift") where a
 * code is expected. Bulk imports have the same exposure. Resolving both means every configured
 * shift behaves the same way, and no shift needs its own branch.
 *
 * Case- and whitespace-insensitive throughout, because both fields are free text typed in Shift
 * Configuration. Code is tried before name so a shift whose name happens to equal another's code
 * cannot hijack it. A value matching neither is still unresolved — inventing a colour for a shift
 * that no longer exists would hide a real data problem.
 */
export function resolveConfiguredShift(
  value: string | undefined,
  shifts: ShiftMaster[]
): ShiftMaster | undefined {
  const wanted = norm(value);
  if (!wanted) return undefined;
  const active = shifts.filter(s => s.isActive);
  return active.find(s => norm(s.shiftCode) === wanted)
    || active.find(s => norm(s.shiftName) === wanted);
}

/**
 * Whole-cell styling for a shift day, from the configured ShiftMaster.
 *
 * Resolved through resolveConfiguredShift, so the colour and the staleness warning can never
 * disagree about whether a stored value names a real shift. A value with no active shift behind it
 * returns no background so the caller can draw it as stale rather than inventing a colour for
 * something that no longer exists.
 */
export function shiftCellDisplay(
  shiftCode: string | undefined,
  shifts: ShiftMaster[],
  isDark = false,
  strength: ToneStrength = 'fill'
): ShiftCellDisplay {
  if (!norm(shiftCode)) return { text: 'inherit', isStale: false };

  const match = resolveConfiguredShift(shiftCode, shifts);
  // No such active shift: stale. Painting it would hide a real data error.
  if (!match) return { text: 'inherit', isStale: true };

  /*
   * The shift exists but has no colour. Previously this shared the stale branch and returned no
   * background — which the roster's own staleness check did *not* flag, because that only asks
   * whether the shift exists. The result was a cell with no colour, no chip and no warning: the
   * only way this screen can fail silently. It now paints a derived stand-in and says so.
   */
  const usableColor = (match.color || '').trim();
  const seed = usableColor || fallbackSeedForCode(match.shiftCode);
  const tone = toneFrom(seed, isDark, strength);

  return {
    background: tone.background,
    text: tone.text,
    border: tone.border,
    label: match.shiftName,
    seed,
    isStale: false,
    ...(usableColor ? {} : { usingFallbackColor: true }),
  };
}

/** Parse #rgb / #rrggbb into 0-255 components. Returns null for anything else. */
function parseHex(color: string): [number, number, number] | null {
  const hex = (color || '').trim().replace(/^#/, '');
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(hex)) return null;
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

const toHex = (v: number): string =>
  Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');

interface Hsl {
  /** Degrees, 0-360. */
  h: number;
  /** 0-1. */
  s: number;
  /** 0-1. */
  l: number;
}

/**
 * sRGB hex to HSL.
 *
 * Used so a tone can be re-lit without touching its hue — the property that makes "GEN stays
 * recognisably blue" a guarantee rather than something to verify by eye after every palette edit.
 * Returns null for an unparseable seed, so callers degrade rather than painting garbage.
 */
export function hexToHsl(color: string): Hsl | null {
  const rgb = parseHex(color);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(v => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l };

  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;

  return { h, s, l };
}

/** HSL back to sRGB hex. Inverse of hexToHsl. */
export function hslToHex({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = l - c / 2;

  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  return `#${rgb.map(channel => toHex((channel + m) * 255)).join('')}`;
}

/**
 * Blend `amount` of `color` into `towards`, in sRGB.
 *
 * sRGB rather than a perceptual space on purpose: these are small tints of already-chosen brand
 * colours, and the naive blend keeps the result predictable from the seed — which matters when
 * someone picks a colour in Shift Configuration and wants to recognise it in the grid. An
 * unparseable seed returns `towards` so a bad config value degrades to the plain surface rather
 * than to black.
 */
export function mix(color: string, towards: string, amount: number): string {
  const a = parseHex(color);
  const b = parseHex(towards);
  if (!a) return towards;
  if (!b) return color;
  const t = Math.max(0, Math.min(1, amount));
  return `#${a.map((channel, i) => toHex(channel * t + b[i] * (1 - t))).join('')}`;
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function relativeLuminance(color: string): number {
  const rgb = parseHex(color);
  if (!rgb) return 1;
  const [r, g, b] = rgb.map(v => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG AA for normal-size text. Roster codes are ~10.5px, so the large-text 3:1 does not apply. */
export const AA_CONTRAST = 4.5;

export const TEXT_ON_LIGHT = '#111827';
export const TEXT_ON_DARK = '#ffffff';

/** WCAG contrast ratio between two colours, 1:1 to 21:1. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Near-black or near-white text, whichever actually contrasts better against `background`.
 *
 * Compares the two candidates rather than thresholding luminance. A fixed cut-off gets
 * mid-tone colours wrong in both directions — at a 0.45 cut, Maternity Leave #8b5cf6 was
 * given dark text at 3.47:1 when white would have given 4.23:1. Both shift and leave colours
 * are HR-editable, so the mid-tones are not hypothetical.
 */
export function readableTextOn(background: string): string {
  return contrastRatio(background, TEXT_ON_LIGHT) >= contrastRatio(background, TEXT_ON_DARK)
    ? TEXT_ON_LIGHT
    : TEXT_ON_DARK;
}

/**
 * Rough perceptual distance between two colours, 0 (identical) to ~255.
 *
 * Weighted per-channel rather than a plain Euclidean RGB distance so it tracks how different
 * two pastels actually *look*; used to assert shift and leave palettes stay apart.
 */
export function colorDistance(a: string, b: string): number {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return Number.POSITIVE_INFINITY;
  const rMean = (ca[0] + cb[0]) / 2;
  const dr = ca[0] - cb[0];
  const dg = ca[1] - cb[1];
  const db = ca[2] - cb[2];
  // Low-cost approximation of a redmean colour difference.
  return Math.sqrt(
    (2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db
  );
}

/**
 * Below this, two fills are close enough that a viewer scanning a month of cells reads them as
 * the same thing.
 *
 * Calibrated on measured values rather than taste. The pairs that prompted the requirement came
 * out at 59 (old Work From Home #e0e7ff against General Shift #c7d2fe) and 66 (old On Duty
 * #dcfce7 against Early Morning Shift #bbf7d0). The new leave palette's closest approach to any
 * configured shift colour is 254, so 90 separates the two regimes with a wide margin either
 * side and is not tuned to sit just above one bad pair.
 */
export const CONFUSABLE_DISTANCE = 90;

export function isConfusable(a: string, b: string): boolean {
  return colorDistance(a, b) < CONFUSABLE_DISTANCE;
}

/**
 * An active shift already using a colour too close to `candidate`, if there is one.
 *
 * Compares the configured seeds, never the painted fills. A fill is a fraction of its seed mixed
 * into the surface, which scales the distance between any two colours down by that same fraction —
 * so testing fills against a threshold calibrated on full-strength colours flagged almost
 * everything, including colours that are obviously different. Two clearly distinct pastels measure
 * around 25 as light-mode fills and would trip a 90 threshold every time.
 *
 * `excludeShiftId` skips the shift being edited, which is otherwise trivially identical to itself.
 * Inactive shifts are ignored: they draw nothing in the grid, so their colour cannot be confused
 * with anything.
 */
export function findConfusableShift(
  candidate: string,
  shifts: ShiftMaster[],
  excludeShiftId?: string
): ShiftMaster | undefined {
  if (!parseHex(candidate)) return undefined;
  return shifts.find(s =>
    s.isActive
    && !!s.color
    && s.id !== excludeShiftId
    && isConfusable(candidate, s.color)
  );
}
